/**
 * Video Effects (tool id `video-effects`): PixVerse effect templates run on
 * Solmi's own PixVerse account. A run is PixVerse's image-to-video call with
 * the template's `template_id` (docs.platform.pixverse.ai/video-effects-796054m0):
 * one photo in, one short clip out, at 720p.
 *
 * Oct 9 2026: these replaced the Kling, Grok and Seedance effect presets
 * resold through Renderful. Those stay in shared/mobbiVideoEffects.json only
 * so jobs started before the switch still poll and keep their names. PixVerse
 * lists every template with a preview video, which the page's Mobbi-style
 * grid needs (mobbi.ai/video-effects); most of the old presets had none.
 *
 * shared/pixverseVideoEffects.json is written by
 * scripts/creative-effects/pixverse-effects.mts from PixVerse's template list
 * and the hand curation in scripts/creative-effects/curation.json: which
 * templates are offered, their family, display name, and the Hot picks.
 * Offered: single-photo templates that suit performers, at most 180 credits.
 * Left out: pet-only, seasonal (Mother's Day, Easter, Lunar New Year…),
 * film, game and brand names, guns and gore, multi-photo and "workflow"
 * templates. The media are re-encoded from PixVerse's previews onto Solmi's
 * CDN: a poster and a small muted loop for the grid, and a larger muted
 * preview for the effect dialog.
 */
import catalog from "./pixverseVideoEffects.json";

/** The filter pills, in order (after All and Hot). */
export const creativeEffectFamilies = [
  { id: "dance", label: "Dance" },
  { id: "stage", label: "Stage" },
  { id: "transform", label: "Transform" },
  { id: "action", label: "Action" },
  { id: "fantasy", label: "Fantasy" },
  { id: "sports", label: "Sports" },
  { id: "funny", label: "Funny" },
  { id: "love", label: "Love" },
] as const;
export type CreativeEffectFamily = (typeof creativeEffectFamilies)[number]["id"];

type CatalogEffect = {
  /** PixVerse template_id (fits a JS number; kept as a string). */
  templateId: string;
  name: string;
  family: CreativeEffectFamily;
  hot?: boolean;
  /** Length of the template's clip; PixVerse fixes it, not the request. */
  seconds: number;
  /** PixVerse credits per 720p run (their template list, credit_consume). */
  pixverseCredits: number;
  /** When PixVerse published the template (YYYY-MM-DD). */
  createdAt: string;
  /** The preview's frame size, so the dialog can size it before it loads. */
  width: number;
  height: number;
  poster: string;
  /** Small muted loop the grid plays (360 px wide). */
  loop: string;
  /** Larger muted preview for the effect dialog (540 px wide). */
  preview: string;
};

export type CreativeEffect = CatalogEffect & {
  /** The creative model id, `pv-fx-<templateId>`. */
  id: string;
  /** Solmi credits per run. */
  credits: number;
  /** Published within NEW_DAYS of the catalog snapshot. */
  isNew: boolean;
};

export const PIXVERSE_EFFECT_MODEL_PREFIX = "pv-fx-";

/**
 * Solmi credits for a PixVerse price, at the rate the PixVerse V6 tiers
 * already charge: 45 PixVerse credits for a 720p 5 s clip sell for 33 credits
 * (rf-i2v-pixverse-v6 in shared/mobbiOutputSettings.json).
 */
export const pixverseEffectCredits = (pixverseCredits: number) =>
  Math.ceil((pixverseCredits * 11) / 15);

/** "New" is relative to the snapshot, so it never differs between server and browser. */
const NEW_DAYS = 21;
const snapshotAt = Date.parse(catalog.fetchedAt);

export const creativeEffects: CreativeEffect[] = (
  catalog.effects as CatalogEffect[]
).map((effect) => ({
  ...effect,
  id: `${PIXVERSE_EFFECT_MODEL_PREFIX}${effect.templateId}`,
  credits: pixverseEffectCredits(effect.pixverseCredits),
  isNew: snapshotAt - Date.parse(effect.createdAt) <= NEW_DAYS * 86_400_000,
}));

export const creativeEffectById: Record<string, CreativeEffect> =
  Object.fromEntries(creativeEffects.map((effect) => [effect.id, effect]));

/** The effect behind a PixVerse template id, for cost records. */
export const creativeEffectByTemplateId = (templateId: string | number) =>
  creativeEffects.find((effect) => effect.templateId === String(templateId));
